import * as Sentry from '@sentry/react';

/**
 * Keeps people working through a deploy.
 *
 * Pages are loaded on demand from files named after their content hash
 * (/assets/InvoiceDetail-abc123.js). A deploy replaces the container that serves
 * them, so a tab opened before it asks for files that no longer exist, and the
 * page fails to load. Reloading picks up the new build, whose files do exist.
 */

// What each browser throws when a page's code, or its CSS, can't be fetched.
const STALE_BUILD_ERRORS = [
  /Failed to fetch dynamically imported module/i, // Chrome
  /error loading dynamically imported module/i, // Firefox
  /Importing a module script failed/i, // Safari
  /Unable to preload CSS/i,
  /ChunkLoadError/,
  /Loading (CSS )?chunk/i
];

export const isStaleBuildError = (error: unknown) => {
  const { name = '', message = '' } = (error ?? {}) as { name?: string; message?: string };
  return STALE_BUILD_ERRORS.some((pattern) => pattern.test(`${name}: ${message}`));
};

const RECOVERY_STATE_KEY = '__deploy_recovery_state';
// Wait before each reload. The first is immediate; the retries give a deploy that is still
// rolling out (or a cache that still holds a "not found") time to settle. After the last
// one the error screen is shown, so a file that really is missing can't loop forever.
export const RELOAD_BACKOFF_MS = [0, 3 * 1000, 10 * 1000, 30 * 1000];
// A new attempt series starts when the last one is older than this (a later deploy).
const RECOVERY_RESET_MS = 5 * 60 * 1000;

type RecoveryState = { attempts: number; at: number };

const readState = (): RecoveryState => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(RECOVERY_STATE_KEY) ?? 'null') as Partial<RecoveryState> | null;
    const attempts = Number(parsed?.attempts) || 0;
    const at = Number(parsed?.at) || 0;
    return Date.now() - at > RECOVERY_RESET_MS ? { attempts: 0, at: 0 } : { attempts, at };
  } catch {
    // Storage can be unavailable (private windows) or hold junk: start over. Without
    // storage the attempts can't be counted across reloads, so the retries are skipped
    // (see reloadForNewBuild) rather than risking a loop.
    return { attempts: 0, at: 0 };
  }
};

const writeState = (state: RecoveryState) => {
  try {
    sessionStorage.setItem(RECOVERY_STATE_KEY, JSON.stringify(state));
    return true;
  } catch {
    return false;
  }
};

let reloadTimer: ReturnType<typeof setTimeout> | undefined;

/**
 * Reload to pick up the latest build, retrying with a backoff (now, 3 s, 10 s, 30 s) when
 * the previous reloads did not help. Returns whether a reload is under way; false means
 * the attempts are used up and the caller should show its error screen.
 */
export const reloadForNewBuild = (reason: string) => {
  // Several error boundaries can report the same failure: one reload is enough.
  if (reloadTimer !== undefined) {
    return true;
  }
  const { attempts } = readState();
  if (attempts >= RELOAD_BACKOFF_MS.length) {
    return false;
  }
  if (!writeState({ attempts: attempts + 1, at: Date.now() })) {
    // Can't remember attempts: a single reload is safe only when it is the first, and we
    // can't tell, so don't reload at all and let the error screen's button do it.
    return false;
  }

  // Counted, so a deploy's effect on open tabs shows up in Sentry.
  Sentry.captureMessage('Reloading for a new build', {
    level: 'info',
    tags: { reason, attempt: String(attempts + 1) }
  });
  reloadTimer = setTimeout(() => {
    void Sentry.flush(2000).finally(() => window.location.reload());
  }, RELOAD_BACKOFF_MS[attempts]);
  return true;
};

let isNewBuildAvailable = false;

/** Whether a newer build than this tab's has been deployed. */
export const newBuildAvailable = () => isNewBuildAvailable;

/** Install once, at startup. Returns a function that removes the listeners again. */
export const installDeployRecovery = () => {
  // Vite's own signal that a page's code or CSS couldn't be fetched. Handled here,
  // the error never reaches the page, so nobody sees "Something went wrong".
  const onPreloadError = (event: Event) => {
    if (reloadForNewBuild('preload')) {
      event.preventDefault();
    }
  };
  // Fired by the update-notification plugin once the server has a newer build.
  const onUpdateNotice = () => {
    isNewBuildAvailable = true;
  };

  window.addEventListener('vite:preloadError', onPreloadError);
  document.body.addEventListener('plugin_web_update_notice', onUpdateNotice);
  return () => {
    window.removeEventListener('vite:preloadError', onPreloadError);
    document.body.removeEventListener('plugin_web_update_notice', onUpdateNotice);
  };
};
